import { FormEvent, useRef, useState } from 'react';
import type { CurrentMember } from '../../shared/models';
import type { CreateWorkspaceInput, WorkspaceKind, WorkspaceSettings, WorkspaceSummary } from '../../shared/workspaces';
import { ArchivedWorkspacesPanel } from '../components/ArchivedWorkspacesPanel';
import { useI18n } from '../i18n';
import { localizedError } from '../i18n/errors';

function requestKey(): string {
  return `workspace-create:${crypto.randomUUID()}`;
}

export function NoWorkspacePage({ member, workspaces, canCreateWorkspace, onCreateWorkspace, onWorkspaceRestored, onLogout }: {
  member: CurrentMember;
  workspaces: WorkspaceSummary[];
  canCreateWorkspace: boolean;
  onCreateWorkspace: (input: CreateWorkspaceInput) => Promise<void>;
  onWorkspaceRestored: (workspace: WorkspaceSettings) => Promise<void>;
  onLogout: () => Promise<void>;
}) {
  const { locale } = useI18n();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<WorkspaceKind>('restaurant');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const createRequestKey = useRef(requestKey());

  function changeName(value: string) {
    setName(value);
    if (!busy) createRequestKey.current = requestKey();
  }

  function changeKind(value: WorkspaceKind) {
    setKind(value);
    if (!busy) createRequestKey.current = requestKey();
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (busy || !name.trim() || !canCreateWorkspace) return;
    setBusy(true); setError('');
    try {
      await onCreateWorkspace({ name: name.trim(), kind, requestKey: createRequestKey.current });
    } catch (err) {
      setError(localizedError(err, locale, {
        'zh-CN': '创建空间失败，请重试。',
        en: 'Failed to create the Workspace. Please retry.'
      }));
    } finally { setBusy(false); }
  }

  return <div className="invite-screen">
    <main className="invite-card no-workspace-card">
      <span className="eyebrow">{locale === 'zh-CN' ? '空间' : 'Workspace'}</span>
      <h1>{locale === 'zh-CN' ? '暂时没有可用空间' : 'No accessible workspace'}</h1>
      <p className="no-workspace-copy">{locale === 'zh-CN'
        ? `账号 ${member.email} 可以正常登录，但目前没有可进入的空间。你可以恢复已归档的空间、接受新的邀请${canCreateWorkspace ? '，也可以创建新空间。' : '。'} `
        : `The Account ${member.email} is still active; it simply has no active Workspace Membership. You can restore an archived Workspace you still own, join through a new invitation${canCreateWorkspace ? ', or create a new Workspace.' : '. Creating a new Workspace requires platform-level entitlement.'}`}</p>
      {error && <p className="form-error">{error}</p>}
      <ArchivedWorkspacesPanel workspaces={workspaces} onRestored={onWorkspaceRestored} />
      {canCreateWorkspace ? <form className="invite-action-panel" onSubmit={create}>
        <div><strong>{locale === 'zh-CN' ? '新建空间' : 'Create a workspace'}</strong><p>{locale === 'zh-CN' ? '创建后你会成为这个空间的主管理员。网络失败可直接重试，不会重复创建。' : 'You will become Owner of the new Workspace; retrying after a network failure will not create a duplicate.'}</p></div>
        <label>{locale === 'zh-CN' ? '空间名称' : 'Workspace name'}<input maxLength={120} value={name} disabled={busy} onChange={event => changeName(event.target.value)} required /></label>
        <label>{locale === 'zh-CN' ? '类型' : 'Type'}<select value={kind} disabled={busy} onChange={event => changeKind(event.target.value as WorkspaceKind)}><option value="restaurant">{locale === 'zh-CN' ? '餐厅' : 'Restaurant'}</option><option value="rnd_kitchen">{locale === 'zh-CN' ? '研发厨房' : 'R&D Kitchen'}</option><option value="test_kitchen">{locale === 'zh-CN' ? '测试厨房' : 'Test Kitchen'}</option></select></label>
        <button className="primary-button" disabled={busy || !name.trim()}>{busy ? (locale === 'zh-CN' ? '创建中…' : 'Creating…') : (locale === 'zh-CN' ? '创建并进入' : 'Create and enter')}</button>
      </form> : <div className="invite-action-panel">
        <strong>{locale === 'zh-CN' ? '等待邀请' : 'Wait for a Workspace invitation'}</strong>
        <p>{locale === 'zh-CN' ? '请让管理员邀请这个邮箱。收到邀请链接后，登录并接受即可。' : 'Ask a Workspace Owner to create an invitation for your email. Open the invitation link, sign in with this Account, and accept it.'}</p>
      </div>}
      <button className="text-button" type="button" disabled={busy} onClick={() => { onLogout().catch(console.error); }}>{locale === 'zh-CN' ? '退出登录' : 'Sign out of this Account'}</button>
    </main>
  </div>;
}
